import { useState } from 'react';
import { CreditCard, Pencil, Plus, Trash } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTableArea,
  CardTitle,
} from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageLoader } from '@/components/ui/Spinner';
import { Switch } from '@/components/ui/Switch';
import {
  Table,
  TableActionsCell,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui/Table';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import type { PaymentMethod } from '@/types/settings';
import { PaymentMethodDialog } from './PaymentMethodDialog';
import {
  useDeletePaymentMethod,
  usePaymentMethods,
  useUpdatePaymentMethod,
} from './use-payment-methods';

/**
 * Métodos de pago del negocio (pestaña "Métodos de pago" de `/settings`, módulo 2).
 *
 * Qué hace el backend con esto: el bot ofrece SOLO los métodos activos y responde con sus
 * `datos` y `condiciones` exactos. Si hay dos o más, el flujo de la orden los lista y pide
 * elegir; si hay uno solo, se asigna solo.
 *
 * La tabla permite activar/desactivar en línea (es la operación más frecuente: un método
 * temporal que se cae). Crear, editar y borrar van por los endpoints de `payment-methods`
 * (todos solo ADMIN: la pantalla esconde los botones y el backend responde 403 igual).
 */

/** Resumen corto de `datos` para la tabla (los objetos largos se cortan con "+N"). */
function summarizeDatos(datos: Record<string, unknown> | null): string[] {
  if (!datos) return [];
  return Object.entries(datos).map(
    ([key, value]) => `${key}: ${typeof value === 'string' ? value : JSON.stringify(value)}`,
  );
}

export function PaymentMethodsTab() {
  const { tenantId, canEdit } = useActiveTenantAccess();
  const methodsQuery = usePaymentMethods(tenantId);
  const updatePaymentMethod = useUpdatePaymentMethod(tenantId);
  const deletePaymentMethod = useDeletePaymentMethod(tenantId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PaymentMethod | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PaymentMethod | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const methods = methodsQuery.data ?? [];

  function openCreate() {
    setEditing(null);
    setActionError(null);
    setDialogOpen(true);
  }

  function openEdit(method: PaymentMethod) {
    setEditing(method);
    setActionError(null);
    setDialogOpen(true);
  }

  /** Activar/desactivar sin abrir el modal (un `PATCH` con un solo campo). */
  async function toggleActive(method: PaymentMethod) {
    setActionError(null);
    try {
      await updatePaymentMethod.mutateAsync({
        paymentMethodId: method.id,
        payload: { activo: !method.activo },
      });
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;

    setActionError(null);
    try {
      await deletePaymentMethod.mutateAsync(pendingDelete.id);
      setPendingDelete(null);
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  }

  return (
    <>
      <Card>
        <CardHeader
          actions={
            canEdit ? (
              <Button size="sm" leftIcon={<Plus size={14} />} onClick={openCreate}>
                Nuevo método
              </Button>
            ) : undefined
          }
        >
          <CardTitle>Métodos de pago</CardTitle>
          <CardDescription>
            {canEdit
              ? 'El bot ofrece solo los métodos activos, con sus datos y condiciones exactos.'
              : 'Solo un administrador del negocio puede crear o editar métodos de pago.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {actionError ? <Alert>{actionError}</Alert> : null}

          {methodsQuery.isPending ? (
            <PageLoader label="Cargando los métodos de pago…" />
          ) : methodsQuery.isError ? (
            <Alert>{getErrorMessage(methodsQuery.error)}</Alert>
          ) : methods.length === 0 ? (
            <EmptyState
              icon={<CreditCard size={22} />}
              title="Todavía no hay métodos de pago"
              description="Agrega al menos uno (Nequi, transferencia, contraentrega…) para que el bot pueda cerrar una orden: sin métodos, el paso del pago se queda a medias."
              action={
                canEdit ? (
                  <Button leftIcon={<Plus size={16} />} onClick={openCreate}>
                    Crear el primero
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <CardTableArea className="-mx-6 -mb-6">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Método</TableHeaderCell>
                    <TableHeaderCell>Datos</TableHeaderCell>
                    <TableHeaderCell>Condiciones</TableHeaderCell>
                    <TableHeaderCell>Activo</TableHeaderCell>
                    <TableHeaderCell align="right">Acciones</TableHeaderCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {methods.map((method) => {
                    const datos = summarizeDatos(method.datos);

                    return (
                      <TableRow key={method.id} className="hover:bg-surface-muted/60">
                        <TableCell>
                          <span className="block font-medium text-ink">{method.nombre}</span>
                          <span className="text-caption text-ink-muted">{method.tipo}</span>
                        </TableCell>

                        <TableCell>
                          {datos.length === 0 ? (
                            <span className="text-ink-faint">—</span>
                          ) : (
                            <span className="flex max-w-md flex-wrap gap-1.5">
                              {datos.slice(0, 3).map((item) => (
                                <span
                                  key={item}
                                  className="rounded-control border border-line bg-surface-muted px-2 py-0.5 text-micro text-ink-soft"
                                >
                                  {item}
                                </span>
                              ))}
                              {datos.length > 3 ? (
                                <span className="px-1 text-micro text-ink-faint">
                                  +{datos.length - 3} más
                                </span>
                              ) : null}
                            </span>
                          )}
                        </TableCell>

                        <TableCell className="max-w-xs">
                          {method.condiciones ? (
                            <span className="line-clamp-2 text-caption text-ink-muted">
                              {method.condiciones}
                            </span>
                          ) : (
                            <span className="text-ink-faint">—</span>
                          )}
                        </TableCell>

                        <TableCell>
                          <span className="flex items-center gap-2.5">
                            <Switch
                              checked={method.activo}
                              label={`Activar o desactivar ${method.nombre}`}
                              disabled={!canEdit || updatePaymentMethod.isPending}
                              onChange={() => void toggleActive(method)}
                            />
                            <Badge tone={method.activo ? 'success' : 'neutral'}>
                              {method.activo ? 'Activo' : 'Inactivo'}
                            </Badge>
                          </span>
                        </TableCell>

                        <TableActionsCell>
                          <span className="inline-flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Editar ${method.nombre}`}
                              disabled={!canEdit}
                              onClick={() => openEdit(method)}
                            >
                              <Pencil size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Eliminar ${method.nombre}`}
                              disabled={!canEdit}
                              onClick={() => {
                                setActionError(null);
                                setPendingDelete(method);
                              }}
                            >
                              <Trash size={16} />
                            </Button>
                          </span>
                        </TableActionsCell>

                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardTableArea>
          )}
        </CardContent>
      </Card>

      {canEdit ? (
        <PaymentMethodDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          paymentMethod={editing}
        />
      ) : null}

      {/* Confirmación de borrado: el error del backend se muestra DENTRO del diálogo para que
          el usuario no pierda el contexto de lo que estaba haciendo. */}
      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
        title="Eliminar el método de pago"
        description={
          pendingDelete
            ? `Se va a eliminar «${pendingDelete.nombre}». El bot dejará de ofrecerlo de inmediato. Si solo quieres ocultarlo un tiempo, desactívalo en vez de borrarlo.`
            : ''
        }
        confirmLabel="Eliminar método"
        loading={deletePaymentMethod.isPending}
        error={actionError}
      />
    </>
  );
}


